import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getAllUsers, 
  getEvents, 
  getJobs, 
  getProjects, 
  getArticles 
} from '../../services/firestoreService';
import { generateSitemap, generateRobotsTxt } from '../../utils/sitemapGenerator';
import { SEO_CONFIG } from '../../services/seoService';

const SEOManagement = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sitemapContent, setSitemapContent] = useState('');
  const [robotsContent, setRobotsContent] = useState('');
  const [seoStats, setSeoStats] = useState({});
  const [message, setMessage] = useState('');

  const isAdmin = hasRole('admin');

  useEffect(() => {
    if (isAdmin) {
      loadSEOStats();
      setRobotsContent(generateRobotsTxt());
    }
  }, [isAdmin]);

  const loadSEOStats = async () => {
    try {
      setLoading(true);
      const [users, events, jobs, projects, articles] = await Promise.all([
        getAllUsers(1000).catch(() => []),
        getEvents(1000).catch(() => []),
        getJobs(1000).catch(() => []),
        getProjects(1000).catch(() => []),
        getArticles(1000).catch(() => [])
      ]);

      setSeoStats({
        totalPages: 13, // Static pages
        userProfiles: users.filter(u => u.status === 'approved').length,
        events: events.length,
        jobs: jobs.length,
        projects: projects.length,
        articles: articles.length,
        totalDynamicPages: users.length + events.length + jobs.length + projects.length + articles.length
      });
    } catch (error) {
      console.error('Error loading SEO stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSitemapContent = async () => {
    try {
      setLoading(true);
      setMessage('Generating sitemap...');

      const [users, events, jobs, projects, articles] = await Promise.all([
        getAllUsers(1000).catch(() => []),
        getEvents(1000).catch(() => []),
        getJobs(1000).catch(() => []),
        getProjects(1000).catch(() => []),
        getArticles(1000).catch(() => [])
      ]);

      const sitemap = await generateSitemap({
        users: users.filter(u => u.status === 'approved'),
        events,
        jobs,
        projects,
        articles
      });

      setSitemapContent(sitemap);
      setMessage('✅ Sitemap generated successfully! Copy the content below and save as sitemap.xml in your public folder.');
    } catch (error) {
      console.error('Error generating sitemap:', error);
      setMessage('❌ Error generating sitemap: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadSitemap = () => {
    const blob = new Blob([sitemapContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sitemap.xml';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadRobots = () => {
    const blob = new Blob([robotsContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'robots.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You don't have permission to access SEO management.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">SEO Management</h1>
        
        {/* SEO Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Total Pages</h3>
            <p className="text-3xl font-bold text-blue-600">
              {seoStats.totalPages + (seoStats.totalDynamicPages || 0)}
            </p>
            <p className="text-sm text-blue-700">
              {seoStats.totalPages} static + {seoStats.totalDynamicPages || 0} dynamic
            </p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-green-900 mb-2">Indexed Content</h3>
            <div className="text-sm text-green-700 space-y-1">
              <p>👥 {seoStats.userProfiles || 0} User Profiles</p>
              <p>📅 {seoStats.events || 0} Events</p>
              <p>💼 {seoStats.jobs || 0} Jobs</p>
              <p>🚀 {seoStats.projects || 0} Projects</p>
              <p>📰 {seoStats.articles || 0} Articles</p>
            </div>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">SEO Health</h3>
            <div className="text-sm text-purple-700 space-y-1">
              <p>✅ Meta Tags: Optimized</p>
              <p>✅ Structured Data: Active</p>
              <p>✅ Open Graph: Configured</p>
              <p>✅ Canonical URLs: Set</p>
              <p>✅ Mobile Friendly: Yes</p>
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-md mb-6 ${
            message.includes('✅') ? 'bg-green-100 text-green-700' : 
            message.includes('❌') ? 'bg-red-100 text-red-700' : 
            'bg-blue-100 text-blue-700'
          }`}>
            {message}
          </div>
        )}

        {/* Sitemap Generation */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Sitemap Generation</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 mb-4">
              Generate an XML sitemap for search engines to better crawl and index your website.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={generateSitemapContent}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Generating...' : 'Generate Sitemap'}
              </button>
              
              {sitemapContent && (
                <button
                  onClick={downloadSitemap}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Download sitemap.xml
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Robots.txt */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Robots.txt</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700 mb-4">
              Download the robots.txt file to control how search engines crawl your site.
            </p>
            <button
              onClick={downloadRobots}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              Download robots.txt
            </button>
          </div>
        </div>

        {/* SEO Tips */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">SEO Optimization Tips</h2>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <ul className="list-disc list-inside text-yellow-800 space-y-2">
              <li>Submit your sitemap to Google Search Console and Bing Webmaster Tools</li>
              <li>Regularly update content to keep search engines engaged</li>
              <li>Optimize page loading speeds for better rankings</li>
              <li>Ensure all images have descriptive alt text</li>
              <li>Use internal linking to help search engines understand site structure</li>
              <li>Monitor your site's performance with Google Analytics</li>
              <li>Create high-quality, relevant content for your target keywords</li>
            </ul>
          </div>
        </div>

        {/* Generated Content Display */}
        {sitemapContent && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Generated Sitemap</h2>
            <textarea
              value={sitemapContent}
              readOnly
              className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm"
              placeholder="Generated sitemap will appear here..."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOManagement;
